import { createSelector } from '@reduxjs/toolkit';
import { EquipmentTypes, type IActiveManager, type IProfession } from '@d-orca/shared/trusstor';
import { professionColor } from '@utils/professionColor';
import {
  selectFloors,
  selectManagers,
  selectProfessions,
  selectProfessionsWithActiveTags,
  selectStaticEquipment,
  selectActiveEquipment,
  selectSelectedManagerIds,
} from './monitor.base.selectors';

/** The profession catalogue fields a workforce row reads. */
type ProfessionRecord = Partial<Pick<IProfession, 'tradeGroup' | 'backgroundColor' | 'tradeId' | 'contractor' | 'specialty'>>;

/**
 * One workforce row: a profession on site (`profession`, with its catalogue
 * record `catalog`), its head count and the floors it is on. Shared by the live
 * selector below and the replay's rows at a past minute.
 */
export function toWorkforceRow(
  profession: { _id: string; tradeId?: string; tradeGroup?: string },
  catalog: ProfessionRecord | undefined,
  active: number,
  floors: string[],
) {
  // Selector exposes the raw trade fields; the chip-text resolution
  // (specialty → translated tradeId → tradeGroup) happens at render
  // time in WorkforceTab where the i18n locale is available. Mirrors
  // trusstor's `ProfessionDisplayWithTradeIcon` flow.
  const specialty = catalog?.specialty;
  const tradeColor = professionColor(catalog?.backgroundColor);
  const tradeId = catalog?.tradeId ?? profession.tradeId;
  const tradeGroup = catalog?.tradeGroup ?? profession.tradeGroup;
  const contractor = catalog?.contractor;
  return {
    // Profession `_id` is the stable React key. tradeGroup repeats across
    // multiple professions ("construction", "systems", etc.) so it's not
    // safe as a key on its own.
    id: profession._id,
    /** Specialty when the profession is configured to show one. */
    specialty,
    /** Specific trade identifier — used both for the chip pictogram
     *  (TradeIcon) and to look up the localized chip text via
     *  `tradeLabel(t, tradeId)` against the i18next `trades.*` namespace
     *  table. */
    tradeId,
    /** Broad category ("construction", "systems"). Last-resort label
     *  fallback when the tradeId has no translation entry. */
    tradeGroup,
    tradeColor,
    /** Sub-trade label rendered after the chip pill (e.g. "JFSix",
     *  "Bounding"). Mirrors trusstor's ProfessionDisplayWithTradeIcon
     *  contractor slot. */
    contractor,
    active,
    onSite: active,
    manager: '', // populated when manager-profession join is needed
    floors,
  };
}

export type WorkforceRow = ReturnType<typeof toWorkforceRow>;

/**
 * Workforce rows derive from professionsWithActiveTags joined with profession metadata.
 * Each row: profession tradeGroup (display name), backgroundColor, active count
 * (= activeTagsIds.length), onSite count (alias of active for now), and which floors they're on.
 */
export const selectWorkforceRows = createSelector(
  [selectProfessionsWithActiveTags, selectProfessions, selectFloors, selectManagers],
  (pwa, professions, floors, _managers) => {
    const profById = new Map(professions.map((p) => [p._id, p]));
    return pwa.map((profession) =>
      toWorkforceRow(
        profession,
        profById.get(profession._id),
        profession.activeTagsIds.length,
        floors
          .filter((floor) => floor.activeProfessions.some((active) => active._id === profession._id))
          .map((floor) => (floor.floorNick as string | undefined) ?? (floor.floorId as string)),
      ),
    );
  },
);

// ---------------------------------------------------------------------------
// Manager presence selectors
//
// Trusstor renders a chip per manager on each floor (initials avatar +
// name pill, see `ManagerChip.tsx` + `FloorManagers.tsx`). Managers live
// on `floor.managers` (and `area.managers` for the per-area drawer view).
// ---------------------------------------------------------------------------

export type ManagerPresence = {
  /** Tag id — stable React key (managers are tagged like workers). */
  id: string;
  name: string;
  /** From IManager.backgroundColor — drives both the avatar disk and the
   *  pill background. */
  backgroundColor: string;
};

function toManagerPresence(
  // IManagerMergedTag has `manager: IManager` with name/backgroundColor.
  // Accept a loose shape so we don't have to re-import the type here.
  tag: { tagId: string; manager?: { _id?: string; name?: string; backgroundColor?: string } },
): ManagerPresence | null {
  const m = tag.manager;
  if (!m?.name) return null;
  return {
    id: tag.tagId,
    name: m.name,
    backgroundColor: professionColor(m.backgroundColor),
  };
}

/** Row id used by `selectManagerListRows` for the LeftRail Managers section
 *  (manager `_id` with a name fallback). Reused by the per-floor selector
 *  so the filter set keys line up exactly with what the LeftRail dispatches. */
function managerRowId(
  tag: { manager?: { _id?: string; name?: string } },
): string | null {
  const m = tag.manager;
  if (!m) return null;
  return m._id ?? m.name ?? null;
}

// ---------------------------------------------------------------------------
// LeftRail "Workforce" tab — list-level rows for the three collapsible
// sections (Professions, Managers, Equipment). Mirrors trusstor_dashboard's
// `WorkForceTab.tsx`, which renders ProfessionsFilter + ManagersFilter +
// EquipmentTypeFilter in a single side panel.
// ---------------------------------------------------------------------------

export type ManagerListRow = {
  id: string;
  name: string;
  /** Trade label — "Foreman", "Safety Manager", etc. From IManager.tradeGroup. */
  role: string;
  backgroundColor: string;
};

/** The site manager list's fields a manager row reads. */
export type ManagerRecord = Partial<Pick<IActiveManager, '_id' | 'name' | 'tradeGroup' | 'backgroundColor' | 'disconnected'>>;

/** A named manager as a LeftRail row: keyed by `_id` (name as fallback), as the filter ids are. */
export const toManagerListRow = (manager: ManagerRecord & { name: string }): ManagerListRow => ({
  id: manager._id ?? manager.name,
  name: manager.name,
  role: manager.tradeGroup ?? '',
  backgroundColor: professionColor(manager.backgroundColor),
});

/** Sort by name (trusstor sorts by sortIndex first, but the wire type
 *  doesn't expose sortIndex — fall back to name alone). */
export const sortManagerRows = (rows: ManagerListRow[]): ManagerListRow[] =>
  rows.sort((a, b) => a.name.localeCompare(b.name));

/** Sorted on-site manager list for the LeftRail Managers section.
 *  Trusstor's upstream emits every manager configured for the project
 *  (active + disconnected) under one wire payload, marking the offline
 *  ones with `disconnected: true`. Trusstor's slice filters those out
 *  (`managers.slice.ts:68 -> activeManagers = payload.filter(m => !m.disconnected)`),
 *  so we mirror the same rule here. */
export const selectManagerListRows = createSelector([selectManagers], (managers) =>
  sortManagerRows(
    managers.flatMap((manager) => {
      // Drop disconnected managers — they're configured on the project
      // but not currently on site. Matches trusstor's "activeManagers".
      return manager.name && !manager.disconnected ? [toManagerListRow({ ...manager, name: manager.name })] : [];
    }),
  ),
);

// Trusstor's `othersTradeGroup` constant (see
// `trusstor_dashboard/backend/Common/src/constants/professions.const.ts:11`).
// Inlined here because @d-orca/shared/trusstor doesn't re-export it.
export const OTHERS_TRADE_GROUP = 'others';

/**
 * Four headline counts for the LeftRail LIVE SUMMARY card — all derived
 * from existing live state, no new wire fields.
 *   workersOnSite — sum of profession active counts, excluding the
 *     non-construction `others` trade group.
 *   activeFloors  — floors with at least one worker present.
 *   managers      — on-site managers (disconnected ones already dropped).
 *   activeTrades  — distinct trade ids among active professions.
 */
export const selectLiveSummary = createSelector(
  [selectWorkforceRows, selectManagerListRows, selectFloors],
  (workforce, managers, floors) => {
    const construction = workforce.filter(
      (r) => r.tradeGroup !== OTHERS_TRADE_GROUP,
    );
    const activeTrades = new Set(
      construction
        .map((r) => r.tradeId)
        .filter((id): id is string => Boolean(id)),
    ).size;
    return {
      workersOnSite: construction.reduce((sum, r) => sum + r.active, 0),
      activeFloors: floors.filter((f) =>
        f.activeProfessions.some((p) => p.activeTagsIds.length > 0),
      ).length,
      managers: managers.length,
      activeTrades,
    };
  },
);

export type EquipmentTypeRow = {
  type: EquipmentTypes;
  /** Display label — currently the enum string ("Power Tools", "Heavy Equipment");
   *  trusstor uses `staticEquipment.typeNick` for localised variants but our
   *  pipeline doesn't surface that field. */
  label: string;
  /** Currently-located equipment of this type. */
  activeCount: number;
  /** Project's total catalogued equipment of this type (from staticEquipment). */
  totalCount: number;
};

/** One row per equipment type. Active count from the live merged list,
 *  total count from the static catalogue — matches trusstor's
 *  `EquipmentTypeFilterSection.getEquipmentTypesWithCount`. We deliberately
 *  use the static catalogue as the source of "this project has this type"
 *  so the UI lists every equipment type the project owns even if none
 *  is currently located. */
export const selectEquipmentTypeRows = createSelector(
  [selectStaticEquipment, selectActiveEquipment],
  (staticEq, activeEq): EquipmentTypeRow[] =>
    equipmentTypeRows(countByType(staticEq), countByType(activeEq)),
);

/** Items per equipment type; an item without a type is not counted. */
export function countByType(items: readonly { type?: string }[]): Map<EquipmentTypes, number> {
  const counts = new Map<EquipmentTypes, number>();
  for (const item of items) {
    const type = item.type as EquipmentTypes | undefined;
    if (type) counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return counts;
}

/** The rows from the total and located counts per type, most located first. */
export function equipmentTypeRows(
  totalByType: Map<EquipmentTypes, number>,
  activeByType: Map<EquipmentTypes, number>,
): EquipmentTypeRow[] {
  // Union of both maps so equipment that's only in `active` (no static
  // catalogue entry) still shows up.
  const allTypes = new Set<EquipmentTypes>([
    ...totalByType.keys(),
    ...activeByType.keys(),
  ]);
  const rows: EquipmentTypeRow[] = Array.from(allTypes).map((type) => ({
    type,
    label: type,
    activeCount: activeByType.get(type) ?? 0,
    totalCount: totalByType.get(type) ?? activeByType.get(type) ?? 0,
  }));
  rows.sort((a, b) => b.activeCount - a.activeCount || a.label.localeCompare(b.label));
  return rows;
}

export const selectManagersByFloorId = createSelector(
  [selectFloors, selectSelectedManagerIds],
  (floors, selectedManagerIds) => {
    const mgrFilter = selectedManagerIds.length > 0 ? new Set(selectedManagerIds) : null;
    const out = new Map<string, ManagerPresence[]>();
    for (const f of floors) {
      const fid = f.floorId as string;
      const list: ManagerPresence[] = [];
      for (const t of f.managers ?? []) {
        if (mgrFilter) {
          const rowId = managerRowId(t);
          if (!rowId || !mgrFilter.has(rowId)) continue;
        }
        const p = toManagerPresence(t);
        if (p) list.push(p);
      }
      if (list.length > 0) {
        // Same sort as trusstor's FloorManagers (sortIndex, name) but our
        // IManager doesn't expose sortIndex; fall back to name for
        // deterministic ordering.
        list.sort((a, b) => a.name.localeCompare(b.name));
        out.set(fid, list);
      }
    }
    return out;
  },
);

/** Each on-site manager's colour by tag id — replay paints a past minute's managers with it. */
export const selectManagerColorByTagId = createSelector(
  [selectManagersByFloorId],
  (managersByFloor): ReadonlyMap<string, string> =>
    new Map([...managersByFloor.values()].flat().map((manager) => [manager.id, manager.backgroundColor])),
);

export const selectManagersByFloorAndArea = createSelector(
  [selectFloors, selectSelectedManagerIds],
  (floors, selectedManagerIds) => {
    const mgrFilter = selectedManagerIds.length > 0 ? new Set(selectedManagerIds) : null;
    const out = new Map<string, Map<string, ManagerPresence[]>>();
    for (const f of floors) {
      const fid = f.floorId as string;
      const inner = new Map<string, ManagerPresence[]>();
      for (const area of f.areas ?? []) {
        const list: ManagerPresence[] = [];
        for (const t of area.managers ?? []) {
          if (mgrFilter) {
            const rowId = managerRowId(t);
            if (!rowId || !mgrFilter.has(rowId)) continue;
          }
          const p = toManagerPresence(t);
          if (p) list.push(p);
        }
        if (list.length > 0) {
          list.sort((a, b) => a.name.localeCompare(b.name));
          inner.set(area.areaId.toLowerCase(), list);
        }
      }
      if (inner.size > 0) out.set(fid, inner);
    }
    return out;
  },
);
